import { createServerFn } from '@tanstack/react-start';
import api from '../utils/api';
import { filterActiveProperty } from '../utils/photoUtils';

function extractId(val) {
    if (!val) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
        if (val._id) return String(val._id);
        if (val.id) return String(val.id);
    }
    return String(val);
}

function computeDestinationAggregations(destinationDocs, resData) {
    const typeCounts = new Map();
    const amenityCounts = new Map();
    const activityCounts = new Map();
    const bestForCounts = new Map();
    const destAreaCounts = new Map();

    destinationDocs.forEach((p) => {
        const ptId = extractId(p.propertyType);
        if (ptId) {
            typeCounts.set(ptId, (typeCounts.get(ptId) || 0) + 1);
        }
        (p.amenities || []).forEach((a) => {
            const aId = extractId(a);
            if (aId) amenityCounts.set(aId, (amenityCounts.get(aId) || 0) + 1);
        });
        (p.activities || []).forEach((act) => {
            const actId = extractId(act);
            if (actId) activityCounts.set(actId, (activityCounts.get(actId) || 0) + 1);
        });
        (p.bestFor || []).forEach((bf) => {
            const bfId = extractId(bf);
            if (bfId) bestForCounts.set(bfId, (bestForCounts.get(bfId) || 0) + 1);
        });
        const daId = extractId(p.visibleOn?.destinationArea);
        if (daId) {
            destAreaCounts.set(daId, (destAreaCounts.get(daId) || 0) + 1);
        }
    });

    const updateCounts = (list, countMap) => {
        return (list || [])
            .map((item) => ({
                ...item,
                count: countMap.get(extractId(item)) || 0,
            }))
            .filter((item) => item.count > 0);
    };

    if (resData.aggregatedPropertyTypes) {
        resData.aggregatedPropertyTypes = updateCounts(resData.aggregatedPropertyTypes, typeCounts);
    }
    if (resData.aggregatedAmenities) {
        resData.aggregatedAmenities = updateCounts(resData.aggregatedAmenities, amenityCounts);
    }
    if (resData.aggregatedActivities) {
        resData.aggregatedActivities = updateCounts(resData.aggregatedActivities, activityCounts);
    }
    if (resData.aggregatedBestFor) {
        resData.aggregatedBestFor = updateCounts(resData.aggregatedBestFor, bestForCounts);
    }
    if (resData.aggregatedDestinationAreas) {
        resData.aggregatedDestinationAreas = updateCounts(resData.aggregatedDestinationAreas, destAreaCounts);
    }
}

const countryDocsCache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000;

async function getCachedCountryDocs(countryInput) {
    const countryId = extractId(countryInput);
    if (!countryId) return { docs: [], resData: {} };

    const cached = countryDocsCache.get(countryId);
    const now = Date.now();

    if (cached && now - cached.timestamp < CACHE_TTL_MS) {
        return {
            docs: JSON.parse(JSON.stringify(cached.docs)),
            resData: JSON.parse(JSON.stringify(cached.resData)),
        };
    }

    const firstResponse = await api('get', 'property/search', { country: countryId, page: 1 });
    const resData = firstResponse.data || {};
    const totalPages = resData.paginatedPropertyList?.totalPages || 1;
    let docs = resData.paginatedPropertyList?.docs || [];

    if (totalPages > 1) {
        const remainingResponses = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) =>
                api('get', 'property/search', { country: countryId, page: i + 2 }).catch(() => null)
            )
        );
        const extraDocs = remainingResponses.flatMap(
            (r) => r?.data?.paginatedPropertyList?.docs || []
        );
        docs = [...docs, ...extraDocs];
    }

    countryDocsCache.set(countryId, {
        docs,
        resData,
        timestamp: now,
    });

    return {
        docs: JSON.parse(JSON.stringify(docs)),
        resData: JSON.parse(JSON.stringify(resData)),
    };
}

export const searchProperties = createServerFn()
    .validator((data) => data)
    .handler(async ({ data }) => {
        const requestParams = data?.data || data || {};
        const { destination, ...apiParams } = requestParams;
        try {
            const fs = await import('fs');
            fs.appendFileSync('/home/ciira-kibebe/.gemini/antigravity-ide/brain/18f7d94a-7581-4aa2-bf5c-2574eda33522/scratch/search_debug.log', 
                JSON.stringify({ type: 'start', timestamp: new Date().toISOString(), destination, apiParams }) + '\n'
            );
        } catch (e) {}

        if (destination && apiParams.country) {
            const targetDestId = extractId(destination);
            const { docs: allCountryDocs, resData } = await getCachedCountryDocs(apiParams.country);

            let destinationDocs = allCountryDocs.map(filterActiveProperty).filter((p) => {
                const pDestId = extractId(p.visibleOn?.destination);
                return pDestId && targetDestId && pDestId === targetDestId;
            });

            computeDestinationAggregations(destinationDocs, resData);

            if (apiParams.propertyTypes?.length) {
                const types = (Array.isArray(apiParams.propertyTypes)
                    ? apiParams.propertyTypes
                    : [apiParams.propertyTypes]
                ).map(extractId);
                destinationDocs = destinationDocs.filter((p) =>
                    types.includes(extractId(p.propertyType))
                );
            }

            if (apiParams.amenities?.length) {
                const ams = (Array.isArray(apiParams.amenities)
                    ? apiParams.amenities
                    : [apiParams.amenities]
                ).map(extractId);
                destinationDocs = destinationDocs.filter((p) => {
                    const pAms = (p.amenities || []).map(extractId);
                    return ams.some((aId) => pAms.includes(aId));
                });
            }

            if (apiParams.activities?.length) {
                const acts = (Array.isArray(apiParams.activities)
                    ? apiParams.activities
                    : [apiParams.activities]
                ).map(extractId);
                destinationDocs = destinationDocs.filter((p) => {
                    const pActs = (p.activities || []).map(extractId);
                    return acts.some((actId) => pActs.includes(actId));
                });
            }

            if (apiParams.bestFor?.length) {
                const bfs = (Array.isArray(apiParams.bestFor)
                    ? apiParams.bestFor
                    : [apiParams.bestFor]
                ).map(extractId);
                destinationDocs = destinationDocs.filter((p) => {
                    const pBfs = (p.bestFor || []).map(extractId);
                    return bfs.some((bfId) => pBfs.includes(bfId));
                });
            }

            if (apiParams.destinationAreas?.length) {
                const das = (Array.isArray(apiParams.destinationAreas)
                    ? apiParams.destinationAreas
                    : [apiParams.destinationAreas]
                ).map(extractId);
                destinationDocs = destinationDocs.filter((p) =>
                    das.includes(extractId(p.visibleOn?.destinationArea))
                );
            }

            if (apiParams.title) {
                const titleLower = apiParams.title.toLowerCase();
                destinationDocs = destinationDocs.filter((p) =>
                    (p.title || p.name || '').toLowerCase().includes(titleLower)
                );
            }

            if (apiParams.bookDirect === true || apiParams.bookDirect === 'true') {
                destinationDocs = destinationDocs.filter((p) => p.bookDirect === true);
            }

            if (apiParams.verifiedOnly === true || apiParams.verifiedOnly === 'true') {
                destinationDocs = destinationDocs.filter((p) => p.isVerified === true);
            }

            if (apiParams.minPrice) {
                const minVal = parseFloat(apiParams.minPrice);
                destinationDocs = destinationDocs.filter((p) => (p.costPerPersonFrom || 0) >= minVal);
            }

            if (apiParams.maxPrice) {
                const maxVal = parseFloat(apiParams.maxPrice);
                destinationDocs = destinationDocs.filter((p) => (p.costPerPersonFrom || 0) <= maxVal);
            }

            if (apiParams.orderByPrice) {
                destinationDocs.sort((a, b) => {
                    const priceA = a.costPerPersonFrom || 0;
                    const priceB = b.costPerPersonFrom || 0;
                    return apiParams.orderByPrice === 'asc' ? priceA - priceB : priceB - priceA;
                });
            }

            if (apiParams.orderByTitle) {
                destinationDocs.sort((a, b) => {
                    const titleA = (a.title || a.name || '').toLowerCase();
                    const titleB = (b.title || b.name || '').toLowerCase();
                    return apiParams.orderByTitle === 'asc'
                        ? titleA.localeCompare(titleB)
                        : titleB.localeCompare(titleA);
                });
            }

            if (apiParams.orderByRooms) {
                destinationDocs.sort((a, b) => {
                    const roomsA = a.rooms || 0;
                    const roomsB = b.rooms || 0;
                    return apiParams.orderByRooms === 'asc' ? roomsA - roomsB : roomsB - roomsA;
                });
            }

            const page = parseInt(apiParams.page, 10) || 1;
            const pageSize = 10;
            const totalDocs = destinationDocs.length;
            const destTotalPages = Math.ceil(totalDocs / pageSize) || 1;
            const startIndex = (page - 1) * pageSize;
            const pageDocs = destinationDocs.slice(startIndex, startIndex + pageSize);
            const hasNextPage = page < destTotalPages;
            const nextPage = hasNextPage ? page + 1 : null;

            if (resData.paginatedPropertyList) {
                resData.paginatedPropertyList.docs = pageDocs;
                resData.paginatedPropertyList.totalDocs = totalDocs;
                resData.paginatedPropertyList.totalPages = destTotalPages;
                resData.paginatedPropertyList.page = page;
                resData.paginatedPropertyList.hasNextPage = hasNextPage;
                resData.paginatedPropertyList.nextPage = nextPage;
            }
            try {
                const fs = await import('fs');
                fs.appendFileSync('/home/ciira-kibebe/.gemini/antigravity-ide/brain/18f7d94a-7581-4aa2-bf5c-2574eda33522/scratch/search_debug.log', 
                    JSON.stringify({ type: 'end', timestamp: new Date().toISOString(), totalDocs, docsCount: pageDocs.length, filteredDocsTitles: pageDocs.map(d => d.title) }) + '\n'
                );
            } catch (e) {}
            return resData;
        }

        const response = await api('get', 'property/search', apiParams);
        const resData = response.data || {};

        if (Array.isArray(resData.paginatedPropertyList?.docs)) {
            resData.paginatedPropertyList.docs = resData.paginatedPropertyList.docs.map(filterActiveProperty);
        }

        if (Array.isArray(resData.properties)) {
            resData.properties = resData.properties.map(filterActiveProperty);
        }

        return resData;
    });

